(function () {
  if (customElements.get('emarsys-recommend')) return;

  class EmarsysRecommend extends HTMLElement {
    static get observedAttributes() {
      return ['logic', 'count', 'container-id', 'section-id'];
    }

    normalizeNumericId(id) {
      const str = String(id).trim();
      const productGidPrefix = 'gid://shopify/Product/';
      const variantGidPrefix = 'gid://shopify/ProductVariant/';

      if (str.startsWith(productGidPrefix)) {
        return str.slice(productGidPrefix.length);
      }

      if (str.startsWith(variantGidPrefix)) {
        return str.slice(variantGidPrefix.length);
      }

      return str.replace(/^g\//, '');
    }

    isScarabProductId(scarabId) {
      return String(scarabId).trim().startsWith('g/');
    }

    productGid(id) {
      return `gid://shopify/Product/${this.normalizeNumericId(id)}`;
    }

    variantGid(id) {
      return `gid://shopify/ProductVariant/${this.normalizeNumericId(id)}`;
    }

    buildScarabIdLookup(scarabProducts) {
      const byShopifyProductId = new Map();
      const byVariantId = new Map();

      scarabProducts.forEach((product) => {
        const scarabId = String(product?.id ?? '').trim();
        if (!scarabId) return;

        const normalized = this.normalizeNumericId(scarabId);
        if (!normalized) return;

        if (this.isScarabProductId(scarabId)) {
          byShopifyProductId.set(normalized, scarabId);
          return;
        }

        byVariantId.set(normalized, scarabId);
      });

      return { byShopifyProductId, byVariantId };
    }

    applyScarabItemAttribute(slide, scarabLookup) {
      if (!scarabLookup) return;

      const productId =
        slide.getAttribute('data-shopify-product-id')
        || slide.querySelector('[data-product_id]')?.getAttribute('data-product_id');
      const variantId = slide.querySelector('[data-variant_id]')?.getAttribute('data-variant_id');
      const reviewProductId = slide.querySelector('.jdgm-widget[data-id]')?.getAttribute('data-id');

      let scarabId = null;

      if (productId) {
        scarabId = scarabLookup.byShopifyProductId.get(String(productId));
      }

      if (!scarabId && variantId) {
        scarabId = scarabLookup.byVariantId.get(String(variantId));
      }

      if (!scarabId && reviewProductId) {
        scarabId = scarabLookup.byShopifyProductId.get(String(reviewProductId));
      }

      if (scarabId) {
        slide.setAttribute('data-scarabitem', scarabId);
        slide.setAttribute('data-scarab-id-type', this.getScarabCatalogIdType(scarabId));
      }
    }

    getRecommendLimit() {
      const count = parseInt(this.getAttribute('count'), 10) || 8;
      return Math.min(Math.max(count, 2), 10);
    }

    getScarabCatalogIdType(scarabId) {
      if (this.isScarabProductId(scarabId)) {
        return 'product';
      }
      return 'variant';
    }

    connectedCallback() {
      if (!this.getAttribute('logic') || !this.getAttribute('container-id')) return;
      if (this._registered) return;
      this._registered = true;
      this.showLoadingState();
      this.pushRecommend();
    }

    showLoadingState() {
      this.removeAttribute('hidden');
      this.hidden = false;
      this.classList.add('is-loading');
      this.initSwiper();
    }

    pushRecommend() {
      if (typeof ScarabQueue === 'undefined') {
        this.onFailure('scarab_queue_unavailable');
        return;
      }

      const config = {
        logic: this.getAttribute('logic'),
        limit: this.getRecommendLimit(),
        containerId: this.getAttribute('container-id'),
        success: (sc) => this.onSuccess(sc),
        failure: () => this.onFailure('scarab_recommend_failure'),
      };

      ScarabQueue.push(['recommend', config]);
    }

    onSuccess(sc) {
      const products = (sc && sc.page && sc.page.products) || [];
      if (products.length === 0) {
        this.onFailure('scarab_empty_products');
        return;
      }

      this.loadProductsFromPredictive(products).catch(() => {
        this.onFailure('load_products_pipeline_failed');
      });
    }

    shouldKeepSkeletonLoading(config) {
      return config.keepSkeletonLoading === true;
    }

    async loadProductsFromPredictive(scarabProducts) {
      const config = window.emarsysRecommend || {};

      if (this.shouldKeepSkeletonLoading(config)) {
        return;
      }

      const limit = this.getRecommendLimit();
      const scarabSlice = scarabProducts.slice(0, limit);
      const scarabLookup = this.buildScarabIdLookup(scarabSlice);
      const catalogEntries = await this.resolveScarabCatalogEntries(scarabSlice, config);

      if (!catalogEntries.length) {
        throw new Error('no_catalog_entries');
      }

      const skus = catalogEntries.map((entry) => entry.sku).filter(Boolean);
      const predictiveUrl = config.predictiveSearchUrl || window.routes?.predictive_search_url;

      if (!predictiveUrl) {
        throw new Error('no_predictive_search_url');
      }

      const sectionId = config.predictiveSearchSectionId || 'predictive-search';
      const searchTerm = skus.join(' OR ');
      const params = new URLSearchParams({
        q: searchTerm,
        'resources[type]': 'product',
        'resources[limit]': String(Math.min(skus.length, limit)),
        'resources[options][fields]': 'variants.sku',
        section_id: sectionId,
      });

      const response = await fetch(`${predictiveUrl}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`predictive_search_http_${response.status}`);
      }

      const html = await response.text();
      this.injectPredictiveResults(html, catalogEntries, scarabLookup);
      this.classList.remove('is-loading');
      this.initSwiper();
    }

    buildScarabNodeRequests(scarabSlice) {
      const requests = scarabSlice.map((product) => {
        const scarabId = String(product?.id ?? '').trim();
        if (!scarabId) return null;

        const idType = this.getScarabCatalogIdType(scarabId);

        if (idType === 'product') {
          return {
            scarabId,
            idType,
            primaryGid: this.productGid(scarabId),
            fallbackGid: null,
          };
        }

        return {
          scarabId,
          idType,
          primaryGid: this.variantGid(scarabId),
          fallbackGid: this.productGid(scarabId),
        };
      }).filter(Boolean);

      return requests;
    }

    async resolveScarabCatalogEntries(scarabSlice, config) {
      if (!config.storefrontApiUrl) {
        return [];
      }

      const requests = this.buildScarabNodeRequests(scarabSlice);
      if (!requests.length) {
        return [];
      }

      const gids = [...new Set(
        requests.flatMap((request) => {
          const ids = [request.primaryGid];
          if (request.fallbackGid) ids.push(request.fallbackGid);
          return ids;
        }),
      )];

      const query = `
        query EmarsysCatalogEntries($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {
          nodes(ids: $ids) {
            ... on Product {
              id
              variants(first: 1) {
                nodes {
                  sku
                }
              }
            }
            ... on ProductVariant {
              id
              sku
              product {
                id
              }
            }
          }
        }
      `;

      const response = await fetch(config.storefrontApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables: {
            ids: gids,
            country: config.countryCode,
          },
        }),
      });

      if (!response.ok) {
        return [];
      }

      const payload = await response.json();
      if (payload.errors?.length) {
        return [];
      }

      const nodesByGid = new Map();
      (payload.data?.nodes || []).forEach((node) => {
        if (node?.id) {
          nodesByGid.set(node.id, node);
        }
      });

      const entries = requests.map((request) => {
        const primaryNode = nodesByGid.get(request.primaryGid);
        const fallbackNode = request.fallbackGid ? nodesByGid.get(request.fallbackGid) : null;
        const node = primaryNode || fallbackNode;

        if (!node) {
          return null;
        }

        if (node.sku) {
          return {
            scarabId: request.scarabId,
            idType: request.idType,
            sku: node.sku,
            productId: this.normalizeNumericId(node.product?.id),
            variantId: this.normalizeNumericId(node.id),
          };
        }

        const sku = node.variants?.nodes?.[0]?.sku;
        if (!sku) {
          return null;
        }

        return {
          scarabId: request.scarabId,
          idType: request.idType,
          sku,
          productId: this.normalizeNumericId(node.id),
          variantId: null,
        };
      }).filter(Boolean);

      return entries;
    }

    getCatalogEntryForSlide(slide, catalogEntries) {
      const productId =
        slide.getAttribute('data-shopify-product-id')
        || slide.querySelector('[data-product_id]')?.getAttribute('data-product_id');
      const variantId = slide.querySelector('[data-variant_id]')?.getAttribute('data-variant_id');

      if (productId) {
        const byProduct = catalogEntries.find(
          (entry) => String(entry.productId) === String(productId),
        );
        if (byProduct) return byProduct;
      }

      if (variantId) {
        return catalogEntries.find(
          (entry) => entry.variantId && String(entry.variantId) === String(variantId),
        );
      }

      return null;
    }

    applyScarabAttributesFromCatalog(slide, catalogEntry) {
      if (!catalogEntry?.scarabId) return;

      slide.setAttribute('data-scarabitem', catalogEntry.scarabId);
      slide.setAttribute(
        'data-scarab-id-type',
        catalogEntry.idType || this.getScarabCatalogIdType(catalogEntry.scarabId),
      );
    }

    stripProductCardTrackingParams(slide) {
      const alwaysRemove = ['_pos', '_psq', '_ss', '_v'];
      const idType = slide.getAttribute('data-scarab-id-type');
      const stripVariant = idType !== 'variant';
      const paramsToRemove = stripVariant ? [...alwaysRemove, 'variant'] : alwaysRemove;

      slide.querySelectorAll('a[href]').forEach((anchor) => {
        const href = anchor.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
          return;
        }

        try {
          const url = new URL(href, window.location.origin);
          paramsToRemove.forEach((param) => url.searchParams.delete(param));
          const cleaned = url.pathname + url.search + url.hash;

          if (/^https?:\/\//i.test(href)) {
            anchor.setAttribute('href', url.toString());
            return;
          }

          anchor.setAttribute('href', cleaned);
        } catch {
          return;
        }
      });
    }

    ensureVariantInProductLinks(slide, variantId) {
      if (!variantId) return;

      slide.querySelectorAll('a[href]').forEach((anchor) => {
        const href = anchor.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
          return;
        }

        try {
          const url = new URL(href, window.location.origin);
          if (!url.pathname.includes('/products/')) return;

          url.searchParams.set('variant', String(variantId));
          const cleaned = url.pathname + url.search + url.hash;

          if (/^https?:\/\//i.test(href)) {
            anchor.setAttribute('href', url.toString());
            return;
          }

          anchor.setAttribute('href', cleaned);
        } catch {
          return;
        }
      });
    }

    stripPredictiveSearchClasses(root) {
      const nodes = [root, ...root.querySelectorAll('*')];

      nodes.forEach((node) => {
        if (!node.classList?.length) return;

        [...node.classList].forEach((className) => {
          if (className.startsWith('predictive-search')) {
            node.classList.remove(className);
          }
        });
      });
    }

    sortPredictiveItems(items, catalogEntries) {
      const itemsByProductId = new Map();
      const itemsByVariantId = new Map();
      const remaining = [];

      items.forEach((item) => {
        const productId = item.getAttribute('data-shopify-product-id');
        const variantId = item.querySelector('[data-variant_id]')?.getAttribute('data-variant_id');

        if (productId) {
          itemsByProductId.set(String(productId), item);
        }

        if (variantId) {
          itemsByVariantId.set(String(variantId), item);
        }

        remaining.push(item);
      });

      const used = new Set();
      const sorted = [];

      catalogEntries.forEach((entry) => {
        let item = itemsByProductId.get(String(entry.productId));

        if (!item && entry.variantId) {
          item = itemsByVariantId.get(String(entry.variantId));
        }

        if (item && !used.has(item)) {
          used.add(item);
          sorted.push(item);
        }
      });

      remaining.forEach((item) => {
        if (!used.has(item)) {
          sorted.push(item);
        }
      });

      return sorted;
    }

    injectPredictiveResults(html, catalogEntries, scarabLookup) {
      const containerId = this.getAttribute('container-id');
      const container = document.getElementById(containerId);
      const sectionId = window.emarsysRecommend?.predictiveSearchSectionId || 'predictive-search';

      if (!container) {
        throw new Error('container_not_found');
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector(`#shopify-section-${sectionId}`);
      const items = section
        ? section.querySelectorAll('#predictive-search-results-list .predictive-search__list-item')
        : doc.querySelectorAll('#predictive-search-results-list .predictive-search__list-item');

      const sortedItems = this.sortPredictiveItems(Array.from(items), catalogEntries);

      container.innerHTML = '';

      sortedItems.forEach((item) => {
        const slide = item.cloneNode(true);
        this.stripPredictiveSearchClasses(slide);
        slide.classList.add('swiper-slide');
        slide.classList.remove('medium-up--one-quarter', 'grid__item');
        slide.removeAttribute('role');

        const catalogEntry = this.getCatalogEntryForSlide(slide, catalogEntries);
        this.applyScarabAttributesFromCatalog(slide, catalogEntry);
        if (!catalogEntry) {
          this.applyScarabItemAttribute(slide, scarabLookup);
        }
        this.stripProductCardTrackingParams(slide);
        if (slide.getAttribute('data-scarab-id-type') === 'variant' && catalogEntry?.variantId) {
          this.ensureVariantInProductLinks(slide, catalogEntry.variantId);
        }

        container.appendChild(slide);
      });

      if (!container.children.length) {
        throw new Error('predictive_search_no_results');
      }
    }

    onFailure() {
      this.classList.remove('is-loading');
      this.hidden = true;
      this.setAttribute('hidden', '');
    }

    getSwiperOptions(swiperEl) {
      const paginationEl = swiperEl.querySelector('.emarsys-recommend__pagination');

      return {
        slidesPerView: 1.3,
        spaceBetween: 0,
        pagination: paginationEl
          ? {
            el: paginationEl,
            clickable: true,
          }
          : undefined,
        breakpoints: {
          500: {
            slidesPerView: 1.7,
          },
          768: {
            slidesPerView: 4,
          },
        },
      };
    }

    initSwiper() {
      const sectionId = this.getAttribute('section-id');
      if (!sectionId) return;

      const swiperEl = this.querySelector('#swiper-emarsys-' + sectionId);
      if (!swiperEl) return;

      if (typeof Swiper === 'undefined') {
        this.scheduleSwiperInit();
        return;
      }

      if (swiperEl.swiper) {
        swiperEl.swiper.destroy(true, true);
      }

      new Swiper(swiperEl, this.getSwiperOptions(swiperEl));
    }

    scheduleSwiperInit(attempt = 0) {
      if (typeof Swiper !== 'undefined') {
        this.initSwiper();
        return;
      }

      if (attempt >= 40) return;

      setTimeout(() => this.scheduleSwiperInit(attempt + 1), 50);
    }
  }

  customElements.define('emarsys-recommend', EmarsysRecommend);
})();
